import { BASE_URL } from 'services/config'
import { ISupportBotAnswer, ISupportBotTurn, askSupportBot } from '.'

/**
 * Read the bot's answer as it is written, instead of waiting for all of it.
 *
 * WHY NOT AXIOS. This is the one call in the app that must be consumed while
 * it is still arriving, and XHR — which is what axios uses in the browser —
 * only hands over `responseText` in full or in a growing string with no
 * framing. `fetch` gives a `ReadableStream`, so this is deliberately the odd
 * one out rather than an oversight.
 *
 * What that costs, and what is done about it: the axios instance carries the
 * `Authorization` header and the 401 handling, and neither applies here. Both
 * are reproduced below, on purpose and in the same shape, so a session that
 * expires mid-chat behaves exactly as it does everywhere else.
 */

/** Everything the server can send us. See `support_bot/views.py`. */
type StreamEvent =
    | { event: 'delta'; data: { text: string } }
    | { event: 'reset'; data: Record<string, never> }
    | { event: 'final'; data: ISupportBotAnswer }
    | { event: 'error'; data: { detail?: string; status?: number } }

export interface ISupportBotStreamHandlers {
    /** Append this to the bubble. Already safe to show — the server pruned it. */
    onDelta: (text: string) => void
    /**
     * Throw away every delta so far.
     *
     * The server sends this when the text it was streaming turned out not to
     * be the answer — the model was narrating a tool call, or the round
     * failed. Rare, and never optional: without it the member reads a
     * half-thought and the real answer glued together.
     */
    onReset: () => void
}

/** One `\n\n`-delimited SSE block, parsed. Unknown events become null. */
const parseBlock = (block: string): StreamEvent | null => {
    let name = 'message'
    const dataLines: string[] = []
    for (const line of block.split('\n')) {
        if (line.startsWith(':')) continue // keep-alive comment
        if (line.startsWith('event:')) name = line.slice(6).trim()
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (!dataLines.length) return null
    try {
        return { event: name, data: JSON.parse(dataLines.join('\n')) } as StreamEvent
    } catch {
        return null
    }
}

const authHeader = (): Record<string, string> => {
    const stored = window.localStorage.getItem('user')
    if (!stored || stored === 'null') return {}
    try {
        const token = JSON.parse(stored)?.token
        return token ? { Authorization: `JWT ${token} ` } : {}
    } catch {
        return {}
    }
}

/**
 * Ask, and receive the answer in pieces.
 *
 * Resolves with the SAME `ISupportBotAnswer` the buffered endpoint returns —
 * the server's `final` event is that object, and it is authoritative. The
 * deltas are for the eye only: a caller that ignores `onDelta` entirely and
 * renders the resolved value behaves exactly like `askSupportBot`.
 *
 * Falls back to the buffered endpoint whenever the stream cannot be used —
 * an old browser, a proxy that will not pass `text/event-stream`, a response
 * with no readable body. The fallback is not a nicety: this modal is how a
 * member reaches support, and it must not depend on a transport that anything
 * between us and them is free to buffer.
 */
export const askSupportBotStreamed = async (
    payload: {
        question: string
        history?: ISupportBotTurn[]
        conversationId?: number | null
    },
    handlers: ISupportBotStreamHandlers
): Promise<ISupportBotAnswer> => {
    const body = JSON.stringify({
        question: payload.question,
        history: payload.history ?? [],
        ...(payload.conversationId
            ? { conversation_id: payload.conversationId }
            : {}),
    })

    let response: Response
    try {
        response = await fetch(`${BASE_URL}/api/v1/support-bot/ask/stream/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': 'fa-IR',
                'CLIENT-TYPE': 'pwa',
                ...authHeader(),
            },
            body,
        })
    } catch {
        // Never reached the server at all. The buffered call will produce the
        // same network error, and produce it through the app's own error
        // handling rather than a bespoke one.
        return askSupportBot(payload)
    }

    if (response.status === 401) {
        // Mirrors the axios interceptor: an expired session is handled the
        // same way whichever transport noticed it first.
        window.localStorage.clear()
        window.location.assign('/auth/login')
        throw new Error('unauthorized')
    }

    if (!response.ok || !response.body) {
        return askSupportBot(payload)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let final: ISupportBotAnswer | null = null

    // `stream: true` on every decode is what keeps a Persian character split
    // across two network chunks from arriving as two replacement characters —
    // the same class of bug the server hit decoding this stream from the
    // provider.
    for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // A frame is only complete at a blank line; anything after the last
        // one is a partial frame and stays in the buffer.
        const blocks = buffer.split('\n\n')
        buffer = blocks.pop() ?? ''
        for (const block of blocks) {
            const parsed = parseBlock(block)
            if (!parsed) continue
            if (parsed.event === 'delta') handlers.onDelta(parsed.data.text)
            else if (parsed.event === 'reset') handlers.onReset()
            else if (parsed.event === 'final') final = parsed.data
            else if (parsed.event === 'error') {
                const error: any = new Error(parsed.data.detail ?? 'stream error')
                error.response = { status: parsed.data.status ?? 500 }
                throw error
            }
        }
    }

    if (!final) {
        // The connection ended without the event that carries the answer —
        // a proxy cutting an idle-looking stream is the usual reason. Whatever
        // was on screen is not an answer, so it is withdrawn and the question
        // is asked again the boring way.
        handlers.onReset()
        return askSupportBot(payload)
    }
    return final
}
